import type { FilterControlConfig } from '@/types/filters'
import { FileType } from '@/types/enums'
import {
  STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
  DUE_DATE_OPTIONS,
  DEPENDENCY_OPTIONS,
  PRIORITY_FILTER_CONFIG,
  HORIZON_FILTER_CONFIG,
  DATE_RANGE_OPTIONS,
  BOOLEAN_FILTER_OPTIONS,
  MESSAGE_STATUS_OPTIONS,
  READ_STATUS_OPTIONS,
} from '@/lib/config/filter-options'

/**
 * Workspaces filter configurations
 */
export const WORKSPACES_FILTER_CONFIGS: FilterControlConfig<
  Record<string, string | number | symbol>
>[] = [
  {
    key: 'creator',
    label: 'Creator',
    options: [], // Special handling for user selection
  },
  {
    key: 'hasUsers',
    label: 'Has Team Members',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasProjects',
    label: 'Has Projects',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasTasks',
    label: 'Has Tasks',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasMessages',
    label: 'Has Messages',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasDocuments',
    label: 'Has Documents',
    options: BOOLEAN_FILTER_OPTIONS,
  },
]

/**
 * Projects filter configurations
 */
export const PROJECTS_FILTER_CONFIGS: FilterControlConfig<
  Record<string, string | number | symbol>
>[] = [
  {
    key: 'status',
    label: 'Status',
    options: STATUS_OPTIONS,
  },
  {
    key: 'priority',
    label: 'Priority',
    options: PRIORITY_FILTER_CONFIG.options,
  },
  {
    key: 'horizon',
    label: 'Horizon',
    options: HORIZON_FILTER_CONFIG.options,
  },
  {
    key: 'creator',
    label: 'Creator',
    options: [], // Special handling for user selection
  },
  {
    key: 'workspace',
    label: 'Workspace',
    options: [], // Special handling for workspace selection
  },
  {
    key: 'user',
    label: 'Team Member',
    options: [], // Special handling for user selection
  },
  {
    key: 'createdDate',
    label: 'Created Date',
    options: DATE_RANGE_OPTIONS,
  },
  {
    key: 'updatedDate',
    label: 'Updated Date',
    options: DATE_RANGE_OPTIONS,
  },
  {
    key: 'hasTasks',
    label: 'Has Tasks',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasMessages',
    label: 'Has Messages',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'hasDocuments',
    label: 'Has Documents',
    options: BOOLEAN_FILTER_OPTIONS,
  },
]

/**
 * Tasks filter configurations
 */
export const TASKS_FILTER_CONFIGS: FilterControlConfig<Record<string, string | number | symbol>>[] =
  [
    {
      key: 'taskType',
      label: 'Type',
      options: TASK_TYPE_OPTIONS,
    },
    {
      key: 'status',
      label: 'Status',
      options: STATUS_OPTIONS,
    },
    {
      key: 'priority',
      label: 'Priority',
      options: PRIORITY_FILTER_CONFIG.options,
    },
    {
      key: 'horizon',
      label: 'Horizon',
      options: HORIZON_FILTER_CONFIG.options,
    },
    {
      key: 'assignee',
      label: 'Assignee',
      options: [], // Special handling for user selection
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      options: DUE_DATE_OPTIONS,
    },
    {
      key: 'isDependency',
      label: 'Is Dependency',
      options: DEPENDENCY_OPTIONS,
    },
  ]

/**
 * Messages filter configurations
 */
export const MESSAGES_FILTER_CONFIGS: FilterControlConfig<
  Record<string, string | number | symbol>
>[] = [
  {
    key: 'status',
    label: 'Status',
    options: MESSAGE_STATUS_OPTIONS,
  },
  {
    key: 'creator',
    label: 'Creator',
    options: [], // Special handling for user selection
  },
  {
    key: 'workspace',
    label: 'Workspace',
    options: [], // Special handling for workspace selection
  },
  {
    key: 'user',
    label: 'Team Member',
    options: [], // Special handling for user selection
  },
  {
    key: 'project',
    label: 'Project',
    options: [], // Special handling for project selection
  },
  {
    key: 'task',
    label: 'Task',
    options: [], // Special handling for task selection
  },
  {
    key: 'document',
    label: 'Document',
    options: [], // Special handling for document selection
  },
  {
    key: 'directMessage',
    label: 'Direct Message',
    options: BOOLEAN_FILTER_OPTIONS,
  },
  {
    key: 'createdDate',
    label: 'Created Date',
    options: DATE_RANGE_OPTIONS,
  },
  {
    key: 'updatedDate',
    label: 'Updated Date',
    options: DATE_RANGE_OPTIONS,
  },
  {
    key: 'readStatus',
    label: 'Read Status',
    options: READ_STATUS_OPTIONS,
  },
]

/**
 * Documents filter configurations
 */
export const DOCUMENT_FILTER_CONFIGS: FilterControlConfig<
  Record<string, string | number | symbol>
>[] = [
  {
    key: 'type',
    label: 'Document Type',
    options: [
      { value: 'all', label: 'All Types' },
      ...Object.values(FileType).map((type) => ({
        value: type,
        label: type,
      })),
    ],
  },
]
